// Authenticated fetch wrapper — refreshes the token before a call and once on a 401.
// A failed refresh never logs the user out (see auth-refresh.ts): the call goes out with
// the token in hand while it is valid, and throws AuthRefreshFailed once it has expired.
// Import and use instead of raw fetch() for all API calls.

import Keycloak from "keycloak-js";
import { nextAuthAction, secondsUntilExpiry, REFRESH_INTERVAL_MS } from "./auth-refresh";
import { noteAuthRefreshFailed, noteRequestOutcome } from "./request-failures";

const AUTH_DISABLED = import.meta.env.VITE_AUTH_DISABLED === "true";

let _keycloak: Keycloak | null = null;
let _setToken: ((token: string | undefined) => void) | null = null;
let _staticToken: string | null = null;
let _refreshFailedAt: number | null = null;

/** Called once from AuthProvider to wire up the Keycloak instance */
export function initAuthFetch(kc: Keycloak, setToken: (t: string | undefined) => void) {
  _keycloak = kc;
  _setToken = setToken;
}

/**
 * The token could not be refreshed and the one in hand is expired or was just
 * refused. Nothing is signed out: AuthProvider's timer keeps retrying the
 * refresh, and the call can be made again once it succeeds.
 */
export class AuthRefreshFailed extends Error {
  constructor(cause?: unknown) {
    super("Can't reach the sign-in service. Retrying; your session is kept.", { cause });
    this.name = "AuthRefreshFailed";
  }
}

/**
 * A static token (a scoped link token, apps/embed) was refused and there is no
 * Keycloak to refresh it. Carries `status` so a hand-off page can tell an
 * expired link from a failed load, as it does for an ApiError.
 */
export class SessionExpired extends Error {
  readonly status = 401;
  constructor() {
    super("Session expired");
    this.name = "SessionExpired";
  }
}

/** Set a static token for AUTH_DISABLED mode (no refresh needed) */
export function setStaticToken(token: string) {
  _staticToken = token;
}

/**
 * Notified when any API call is refused because every membership has expired.
 *
 * The gateway can start refusing mid-session — an expiry passes while the tab is
 * open, and nothing re-fetches memberships until the next token refresh. Without
 * a central hook the user sits in the full app with every panel erroring, which
 * is the exact state AccessExpiredPage exists to replace. Keyed on the stable
 * `code`, never the prose.
 */
let _onAccessExpired: (() => void) | null = null;
export function setAccessExpiredHandler(fn: (() => void) | null) {
  _onAccessExpired = fn;
}

/**
 * Inspect a 403 for the membership-expiry code. Reads a CLONE — the caller still
 * owns the original body, which is single-use.
 */
async function flagMembershipExpiry(res: Response): Promise<Response> {
  if (res.status !== 403 || !_onAccessExpired) return res;
  try {
    const body = await res.clone().json() as { code?: string } | null;
    if (body?.code === "membership_expired") _onAccessExpired();
  } catch { /* not JSON, or already consumed — nothing to flag */ }
  return res;
}

let _impersonateOrg: string | null = null;
/**
 * While a platform_admin impersonation session is active, scope every upstream
 * request to the impersonated account via the x-impersonate-org header. The
 * gateway honors it only for a verified platform_admin caller (KAN-1820).
 */
export function setImpersonateOrg(orgId: string | null) {
  _impersonateOrg = orgId;
}

/**
 * Get a fresh token, refreshing if needed.
 *
 * `refused` is set on the 401 retry: the server has just rejected the token in
 * hand, so a failed refresh cannot fall back to it. Otherwise a failed refresh
 * is not retried from here for REFRESH_INTERVAL_MS, so a burst of calls during
 * an outage does not become a burst of refresh grants.
 */
async function getFreshToken(refused = false): Promise<string | null> {
  // Static token (embed mode or AUTH_DISABLED): no refresh needed
  if (_staticToken) return _staticToken;

  const kc = _keycloak;
  if (!kc?.token) return null;
  let failure: unknown;
  if (refused || _refreshFailedAt === null || Date.now() - _refreshFailedAt >= REFRESH_INTERVAL_MS) {
    try {
      // Refresh if expires in less than 60 seconds
      const refreshed = await kc.updateToken(60);
      _refreshFailedAt = null;
      if (refreshed && _setToken) _setToken(kc.token);
      return kc.token ?? null;
    } catch (err) {
      _refreshFailedAt = Date.now();
      failure = err;
    }
  }
  if (!refused && kc.token && nextAuthAction(secondsUntilExpiry(kc)).kind === "wait") {
    return kc.token;
  }
  throw new AuthRefreshFailed(failure);
}

/**
 * Fetch with automatic token refresh.
 * On 401: refreshes token and retries once. If that refresh fails, throws
 * AuthRefreshFailed; it never logs the user out.
 *
 * @param devFallback — static token used in DEV_MODE (when Keycloak is not initialised).
 */
export async function authFetch(url: string, options?: RequestInit, devFallback?: string | null): Promise<Response> {
  try {
    const res = await authFetchOnce(url, options, devFallback);
    noteRequestOutcome(url, options, res);
    return res;
  } catch (err) {
    // A network failure. A cancelled request and a refresh that could not
    // reach Keycloak are not this request failing.
    if (err instanceof TypeError) noteRequestOutcome(url, options, null);
    else if (err instanceof AuthRefreshFailed) noteAuthRefreshFailed(url, options);
    throw err;
  }
}

async function authFetchOnce(url: string, options?: RequestInit, devFallback?: string | null): Promise<Response> {
  // An explicit Authorization on the init wins. Callers that act as somebody
  // other than the signed-in user pass one (the widget builder's agent preview
  // runs under the selected customer's embed token); overwriting it here sent
  // their writes out as the admin while their reads used the override, which
  // showed up as the agent creating a thread in one org and then 404ing when
  // the stream looked for it in the other.
  const explicitAuth = new Headers(options?.headers).get("Authorization");
  if (explicitAuth) {
    const headers = new Headers(options?.headers);
    if (_impersonateOrg) headers.set("x-impersonate-org", _impersonateOrg);
    // No 401 re-auth: refreshing the signed-in user's Keycloak token would not
    // help a request that is deliberately not theirs.
    return fetch(url, { ...options, headers });
  }

  let token: string | null;
  if (_staticToken) {
    token = _staticToken;
  } else if (_keycloak) {
    token = await getFreshToken();
  } else {
    // DEV_MODE — Keycloak not initialised, use the static dev token from React state
    token = devFallback ?? null;
  }
  if (!token) throw new Error("Not authenticated");

  const headers = new Headers(options?.headers);
  headers.set("Authorization", `Bearer ${token}`);
  if (_impersonateOrg) headers.set("x-impersonate-org", _impersonateOrg);

  const res = await fetch(url, { ...options, headers });

  if (res.status === 401 && !AUTH_DISABLED) {
    // Token expired between refresh check — try once more
    if (!_keycloak) {
      throw new SessionExpired();
    }
    const newToken = await getFreshToken(true);
    if (!newToken) throw new AuthRefreshFailed();
    headers.set("Authorization", `Bearer ${newToken}`);
    return flagMembershipExpiry(await fetch(url, { ...options, headers }));
  }

  return flagMembershipExpiry(res);
}
